import { useEffect, useMemo, useState } from 'react';
import {
  CC_STATUS_LABELS,
  ccFormatElapsed,
  type CallCenterDashboard,
  type CallCenterStatus,
} from '@matix/types';
import { useLang } from '../../lib/language';
import { useMoneyFormatter, useServerClock } from './hooks';
import { ccErrorMessage } from './api';

/**
 * The monitoring dashboard, shared by the call centre and the branch.
 *
 * ONE component for both, because they are the same question asked at two
 * scopes — "what is happening right now, and how are we doing today" — and the
 * server already answers it with the same shape either way (`/dashboard` for a
 * supervisor, `/branch/dashboard` for a branch, identical payload). The only
 * difference is whether the branch table is worth drawing, which is what
 * `showBranches` decides.
 *
 * Charts are inline SVG and CSS bars rather than a charting library: this
 * bundle ships to counter tablets over hotel wifi, and none of these five
 * shapes needs axes, tooltips or a legend to be read at a glance from two
 * metres away — which is the actual viewing distance.
 */
export function Monitor({
  load,
  showBranches,
  pollSeconds = 30,
}: {
  load: (range: { from?: string; to?: string }) => Promise<CallCenterDashboard>;
  showBranches: boolean;
  pollSeconds?: number;
}): JSX.Element {
  const { t } = useLang();
  const money = useMoneyFormatter();

  const [data, setData] = useState<CallCenterDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<{ from?: string; to?: string }>({});
  const now = useServerClock(data?.serverTime);

  const rangeKey = `${range.from ?? ''}|${range.to ?? ''}`;

  useEffect(() => {
    let alive = true;
    const run = (): void => {
      void load(range)
        .then((d) => {
          if (alive) {
            setData(d);
            setError(null);
          }
        })
        .catch((err: unknown) => {
          if (alive) setError(ccErrorMessage(err, 'Failed to load'));
        });
    };
    run();
    const id = setInterval(run, Math.max(10, pollSeconds) * 1000);
    return () => {
      alive = false;
      clearInterval(id);
    };
    // `load` is recreated per render by callers; the range is what actually
    // changes the request, so that is what this depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, pollSeconds]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  if (error && !data) {
    return <div className="cc-empty">{error}</div>;
  }
  if (!data) {
    return <div className="cc-empty">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>;
  }

  const k = data.kpis;

  return (
    <div className="cc-monitor">
      <div className="cc-monitor-bar">
        <label className="cc-field cc-field--inline">
          <span>{t({ en: 'From', ar: 'من' })}</span>
          <input
            type="date"
            value={range.from ?? today}
            max={range.to ?? today}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
          />
        </label>
        <label className="cc-field cc-field--inline">
          <span>{t({ en: 'To', ar: 'إلى' })}</span>
          <input
            type="date"
            value={range.to ?? today}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
          />
        </label>
        <button type="button" className="cc-link" onClick={() => setRange({})}>
          {t({ en: 'Today', ar: 'اليوم' })}
        </button>
        <span className="cc-monitor-clock">
          {new Date(now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      <div className="cc-kpis">
        <Kpi label={t({ en: 'Orders', ar: 'الطلبات' })} value={String(k.ordersToday)} />
        <Kpi label={t({ en: 'Sales', ar: 'المبيعات' })} value={money(k.salesToday)} />
        <Kpi label={t({ en: 'Average ticket', ar: 'متوسط الفاتورة' })} value={money(k.averageTicket)} />
        <Kpi label={t({ en: 'Open now', ar: 'مفتوح الآن' })} value={String(k.openOrders)} />
        <Kpi
          label={t({ en: 'Late', ar: 'متأخر' })}
          value={String(k.lateOrders)}
          tone={k.lateOrders > 0 ? 'bad' : 'ok'}
        />
        <Kpi
          label={t({ en: 'Avg prep', ar: 'متوسط التحضير' })}
          value={k.avgPrepSeconds == null ? '—' : ccFormatElapsed(k.avgPrepSeconds)}
        />
        <Kpi
          label={t({ en: 'Avg handover', ar: 'متوسط التسليم' })}
          value={k.avgFulfilSeconds == null ? '—' : ccFormatElapsed(k.avgFulfilSeconds)}
        />
        <Kpi
          label={t({ en: 'Cancelled', ar: 'ملغي' })}
          value={`${k.cancelledToday} (${(k.cancellationRate * 100).toFixed(0)}%)`}
          tone={k.cancellationRate > 0.1 ? 'bad' : 'ok'}
        />
      </div>

      <div className="cc-monitor-grid">
        <Panel title={t({ en: 'Where orders are', ar: 'أين الطلبات' })}>
          <StatusFunnel rows={data.byStatus} />
        </Panel>

        <Panel title={t({ en: 'Orders by hour', ar: 'الطلبات حسب الساعة' })}>
          <HourBars rows={data.byHour} />
        </Panel>

        {showBranches && (
          <Panel title={t({ en: 'Branches', ar: 'الفروع' })} wide>
            <table className="cc-table">
              <thead>
                <tr>
                  <th>{t({ en: 'Branch', ar: 'الفرع' })}</th>
                  <th className="cc-num">{t({ en: 'Orders', ar: 'الطلبات' })}</th>
                  <th className="cc-num">{t({ en: 'Open', ar: 'مفتوح' })}</th>
                  <th className="cc-num">{t({ en: 'Sales', ar: 'المبيعات' })}</th>
                  <th className="cc-num">{t({ en: 'Avg handover', ar: 'متوسط التسليم' })}</th>
                </tr>
              </thead>
              <tbody>
                {data.byBranch.map((b) => (
                  <tr key={b.branchId}>
                    <td>{b.branchName ?? `#${b.branchId}`}</td>
                    <td className="cc-num">{b.orders}</td>
                    <td className="cc-num">{b.open}</td>
                    <td className="cc-num">{money(b.amount)}</td>
                    <td className="cc-num">
                      {b.avgFulfilSeconds == null ? '—' : ccFormatElapsed(b.avgFulfilSeconds)}
                    </td>
                  </tr>
                ))}
                {data.byBranch.length === 0 && (
                  <tr>
                    <td colSpan={5} className="cc-table-empty">
                      {t({ en: 'No orders in this range.', ar: 'لا توجد طلبات في هذه الفترة.' })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Panel>
        )}

        <Panel title={t({ en: 'Payment mix', ar: 'طرق الدفع' })}>
          <ShareBars
            rows={data.byPayment.map((p) => ({
              label: p.label ?? t({ en: 'Unsettled', ar: 'غير محصّل' }),
              value: p.amount,
              caption: `${p.orders}`,
            }))}
            format={money}
          />
        </Panel>

        <Panel title={t({ en: 'Top items', ar: 'الأكثر مبيعاً' })}>
          <ShareBars
            rows={data.topItems.slice(0, 8).map((i) => ({
              label: i.name ?? `#${i.itemId}`,
              value: i.qty,
              caption: money(i.amount),
            }))}
            format={(n) => String(n)}
          />
        </Panel>

        <Panel title={t({ en: 'Agents', ar: 'الوكلاء' })} wide>
          <table className="cc-table">
            <thead>
              <tr>
                <th>{t({ en: 'Agent', ar: 'الوكيل' })}</th>
                <th className="cc-num">{t({ en: 'Orders', ar: 'الطلبات' })}</th>
                <th className="cc-num">{t({ en: 'Sales', ar: 'المبيعات' })}</th>
                <th className="cc-num">{t({ en: 'Cancelled', ar: 'ملغي' })}</th>
              </tr>
            </thead>
            <tbody>
              {data.agents.map((a) => (
                <tr key={a.userId}>
                  <td>{a.name ?? `#${a.userId}`}</td>
                  <td className="cc-num">{a.orders}</td>
                  <td className="cc-num">{money(a.amount)}</td>
                  <td className="cc-num">{a.cancelled}</td>
                </tr>
              ))}
              {data.agents.length === 0 && (
                <tr>
                  <td colSpan={4} className="cc-table-empty">
                    {t({ en: 'No orders in this range.', ar: 'لا توجد طلبات في هذه الفترة.' })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Panel>
      </div>

      <p className="cc-monitor-foot">
        {t({
          en: 'Totals here are the call-centre copy of each order, for speed. The receipt and the Z report remain the record for money.',
          ar: 'الإجماليات هنا نسخة مركز الاتصال من كل طلب لأغراض السرعة. تبقى الفاتورة وتقرير Z هما المرجع للمبالغ.',
        })}
      </p>
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'bad';
}): JSX.Element {
  return (
    <div className={`cc-kpi ${tone === 'bad' ? 'cc-kpi--bad' : ''}`}>
      <div className="cc-kpi-value">{value}</div>
      <div className="cc-kpi-label">{label}</div>
    </div>
  );
}

function Panel({
  title,
  children,
  wide,
}: {
  title: string;
  children: React.ReactNode;
  wide?: boolean;
}): JSX.Element {
  return (
    <section className={`cc-panel ${wide ? 'cc-panel--wide' : ''}`}>
      <h4>{title}</h4>
      {children}
    </section>
  );
}

const FUNNEL_ORDER: CallCenterStatus[] = [
  'new',
  'preparing',
  'ready',
  'out_for_delivery',
  'delivered',
  'cancelled',
];

function StatusFunnel({
  rows,
}: {
  rows: Array<{ status: CallCenterStatus; count: number; amount: number }>;
}): JSX.Element {
  const { t } = useLang();
  const byStatus = new Map(rows.map((r) => [r.status, r]));
  const max = Math.max(1, ...rows.map((r) => r.count));

  return (
    <ul className="cc-funnel">
      {FUNNEL_ORDER.map((status) => {
        const row = byStatus.get(status);
        const count = row?.count ?? 0;
        return (
          <li key={status} className={`cc-funnel-row cc-funnel-row--${status}`}>
            <span className="cc-funnel-label">{t(CC_STATUS_LABELS[status])}</span>
            <span className="cc-funnel-bar">
              <span style={{ width: `${(count / max) * 100}%` }} />
            </span>
            <span className="cc-funnel-n">{count}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Every hour of the day, not only the ones with orders.
 *
 * A chart that skips the empty hours makes a quiet afternoon look like a busy
 * one, and staffing a call centre is precisely the decision this chart is for.
 */
function HourBars({ rows }: { rows: Array<{ hour: number; orders: number }> }): JSX.Element {
  const byHour = new Map(rows.map((r) => [r.hour, r.orders]));
  const max = Math.max(1, ...rows.map((r) => r.orders));

  return (
    <div className="cc-hours">
      {Array.from({ length: 24 }, (_, h) => {
        const n = byHour.get(h) ?? 0;
        return (
          <div key={h} className="cc-hour" title={`${String(h).padStart(2, '0')}:00 — ${n}`}>
            <span className="cc-hour-bar" style={{ height: `${(n / max) * 100}%` }} />
            {h % 6 === 0 && <span className="cc-hour-tick">{h}</span>}
          </div>
        );
      })}
    </div>
  );
}

function ShareBars({
  rows,
  format,
}: {
  rows: Array<{ label: string; value: number; caption?: string }>;
  format: (n: number) => string;
}): JSX.Element {
  const { t } = useLang();
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (rows.length === 0) {
    return <div className="cc-hint">{t({ en: 'Nothing yet.', ar: 'لا شيء بعد.' })}</div>;
  }
  return (
    <ul className="cc-share">
      {rows.map((r) => (
        <li key={r.label}>
          <span className="cc-share-label">{r.label}</span>
          <span className="cc-share-bar">
            <span style={{ width: `${(r.value / max) * 100}%` }} />
          </span>
          <span className="cc-share-val">
            {format(r.value)}
            {r.caption ? <em>{r.caption}</em> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}
